import {
  BookmarkIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/solid"
import clsx from "clsx"
import React from "react"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import ReactQuill from "react-quill"
import NumberInput from "../ui/form/NumberInput"
import TextInput from "../ui/form/TextInput"
import BasicValidationMessage from "../ui/validation/BasicValidationMessage"
import classes from "../../assets/styles/ui/Input.module.css"
import ButtonWithIcon from "../ui/button/ButtonWithIcon"

export type CourseVideoForm = {
  title: string
  sequence: number
  notes: string
  is_preview: boolean
}

interface CourseVideoManagementFormProps {
  maxSeq: number
  serverValidationResponse?: {
    title: string | undefined
    sequence: string | undefined
  }
  submitting?: boolean
  initialValue?: CourseVideoForm
  isUpdate?: boolean
  isDetail?: boolean
  onSubmitHandler: SubmitHandler<CourseVideoForm>
}

const CourseVideoManagementForm = (props: CourseVideoManagementFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<CourseVideoForm>({
    mode: "all",
    defaultValues: props.initialValue,
  })

  return (
    <form onSubmit={handleSubmit(props.onSubmitHandler)}>
      <div className="grid gap-4 sm:grid-cols-2 sm:gap-6">
        <div className="sm:col-span-2">
          <TextInput
            inputClass="cms-base-input min-w-[42rem]"
            labelClass="cms-base-label"
            isRequired
            label="Judul Video"
            name="title"
            value=""
            disabled={props.isDetail}
            hooks={{
              ...register("title", {
                required: "Judul video tidak boleh kosong!",
                minLength: {
                  message: "Judul video minimal 3 karakter!",
                  value: 3,
                },
                maxLength: {
                  message:
                    "Judul module belajar tidak boleh melebihi 255 karakter!",
                  value: 255,
                },
              }),
            }}
            validationMessage={
              errors.title && (
                <BasicValidationMessage
                  type="error"
                  message={errors.title.message as string}
                  icon={
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-800" />
                  }
                />
              )
            }
          />
        </div>
        <div className="sm:col-span-2">
          <NumberInput
            inputClass="cms-base-input min-w-[42rem]"
            isRequired
            label="Urutan Video"
            labelClass="cms-base-label"
            min={1}
            max={props.maxSeq}
            name="sequence"
            value={props.maxSeq}
            hooks={{
              ...register("sequence", {
                required: "Urutan video tidak boleh kosong!",
                min: {
                  message: "Urutan video tidak boleh lebih kecil dari 1!",
                  value: 1,
                },
                max: {
                  message: `Urutan video tidak boleh melebihi ${props.maxSeq}`,
                  value: props.maxSeq,
                },
              }),
            }}
            validationMessage={
              errors.sequence && (
                <BasicValidationMessage
                  type="error"
                  message={errors.sequence.message as string}
                  icon={
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-800" />
                  }
                />
              )
            }
          />
        </div>
        <div className="sm:col-span-2">
          <label className={clsx("cms-base-label", classes.required)}>
            Deskripsi Modul Pembelajaran
          </label>
          <div
            className={clsx(
              "cms-base-input max-w-[42rem]",
              errors.notes && "mb-2"
            )}
          >
            <Controller
              control={control}
              defaultValue=""
              name="notes"
              rules={{
                required: "Deskripsi tidak boleh kosong!",
                minLength: {
                  message: "Deskripsi minimal harus 20 karakter!",
                  value: 20,
                },
              }}
              render={({ field: { onChange, value, ref, onBlur } }) => (
                <ReactQuill
                  ref={ref}
                  onBlur={onBlur}
                  value={value}
                  onChange={(text) => onChange(text)}
                  theme="snow"
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, false] }],
                      ["bold", "italic", "underline", "strike", "blockquote"],
                      [
                        { list: "ordered" },
                        { list: "bullet" },
                        { indent: "-1" },
                        { indent: "+1" },
                      ],
                      ["link"],
                      ["clean"],
                    ],
                  }}
                  formats={[
                    "header",
                    "bold",
                    "italic",
                    "underline",
                    "strike",
                    "blockquote",
                    "list",
                    "bullet",
                    "indent",
                    "link",
                  ]}
                />
              )}
            />
          </div>
          {errors.notes && (
            <BasicValidationMessage
              type="error"
              message={errors.notes.message as string}
              icon={
                <ExclamationTriangleIcon className="h-6 w-6 text-red-800" />
              }
            />
          )}
        </div>
        <div className="sm:col-span-2">
          <TextInput
            type="checkbox"
            className="cms-base-input w-fit bg-slate-100 border-primary checked:text-primary"
            labelClass="cms-base-label w-fit"
            label="Tampilkan Sebagai Preview"
            {...register("is_preview")}
          />
        </div>
      </div>
      {!props.submitting && !props.isDetail && (
        <ButtonWithIcon
          action={undefined}
          type="submit"
          class="mt-4 border-2 border-transparent bg-primary text-white space-x-2 hover:bg-transparent hover:text-white hover:border-primary"
        >
          <BookmarkIcon className="h-6 w-6 text-white" />
          <span>{!props.isUpdate ? "Tambahkan" : "Update"}</span>
        </ButtonWithIcon>
      )}
      {props.submitting && (
        <ButtonWithIcon
          action={undefined}
          type="button"
          class="mt-4 border-2 border-transparent bg-primary text-white space-x-2 cursor-not-allowed"
          disabled
        >
          <svg
            aria-hidden="true"
            className="inline w-6 h-6 mr-2 text-gray-400 animate-spin fill-white"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
          <span>Menyimpan...</span>
        </ButtonWithIcon>
      )}
    </form>
  )
}

export default CourseVideoManagementForm
